/**
 * Grid Codec — pure RLE + bit-packing functions for terrain serialization.
 *
 * No dependencies on grid singleton state. Used by:
 * - grid-serialization.ts (game runtime)
 * - level editor (tools/level-editor/)
 * - stage generator (tools/stage-gen/)
 */

/** RLE-encode a Uint8Array: [value, count, value, count, ...] with max run 255. */
function rleEncode(data: Uint8Array): Uint8Array {
  const out: number[] = [];
  let i = 0;
  while (i < data.length) {
    const val = data[i];
    let run = 1;
    while (i + run < data.length && data[i + run] === val && run < 255) {
      run++;
    }
    out.push(val, run);
    i += run;
  }
  return new Uint8Array(out);
}

/** RLE-decode back to original Uint8Array. */
function rleDecode(rle: Uint8Array, outputLength: number): Uint8Array {
  const out = new Uint8Array(outputLength);
  let oi = 0;
  for (let i = 0; i < rle.length; i += 2) {
    const val = rle[i];
    const run = rle[i + 1];
    for (let j = 0; j < run && oi < outputLength; j++) {
      out[oi++] = val;
    }
  }
  return out;
}

/**
 * Pack a raw grid array into R2 base64 string.
 * R2 format: 2-bit packing (4 cells/byte) → RLE → base64.
 * Supports cell values 0-3 (EMPTY=0, SAND=1, WIREFRAME=2).
 */
export function packR2(grid: Uint8Array): string {
  const packed = new Uint8Array(Math.ceil(grid.length / 4));
  for (let i = 0; i < grid.length; i++) {
    packed[i >> 2] |= (grid[i] & 0x3) << ((i & 3) << 1);
  }
  const rle = rleEncode(packed);
  let binary = '';
  for (let i = 0; i < rle.length; i++) {
    binary += String.fromCharCode(rle[i]);
  }
  return 'R2' + btoa(binary);
}

/**
 * Unpack R2 base64 string into a grid array.
 * R2 format: 2-bit packing + RLE (cell values 0-3).
 */
export function unpackR2(base64: string, grid: Uint8Array): void {
  const binary = atob(base64.slice(2));
  const rle = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    rle[i] = binary.charCodeAt(i);
  }
  const packedLength = Math.ceil(grid.length / 4);
  const packed = rleDecode(rle, packedLength);
  for (let i = 0; i < grid.length; i++) {
    grid[i] = (packed[i >> 2] >> ((i & 3) << 1)) & 0x3;
  }
}

/**
 * Unpack R1 base64 string into a grid array.
 * R1 format: 1-bit packing + RLE (binary solid/empty).
 */
export function unpackR1(base64: string, grid: Uint8Array): void {
  const binary = atob(base64.slice(2));
  const rle = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    rle[i] = binary.charCodeAt(i);
  }
  const packedLength = Math.ceil(grid.length / 8);
  const packed = rleDecode(rle, packedLength);
  for (let i = 0; i < grid.length; i++) {
    grid[i] = (packed[i >> 3] & (1 << (i & 7))) ? 1 : 0;
  }
}

/**
 * Unpack legacy base64 string into a grid array.
 * Legacy format: 1-bit bitpack only (no RLE).
 */
export function unpackLegacy(base64: string, grid: Uint8Array): void {
  const binary = atob(base64);
  const packed = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    packed[i] = binary.charCodeAt(i);
  }
  for (let i = 0; i < grid.length; i++) {
    grid[i] = (packed[i >> 3] & (1 << (i & 7))) ? 1 : 0;
  }
}
